import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';
import QRCode from 'react-qr-code';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import { getApiErrorMessage } from '../../utils/apiError';

interface SegundoFactorTabProps {
  hideTitle?: boolean;
  onEnabled?: () => void;
}

const SegundoFactorTab: React.FC<SegundoFactorTabProps> = ({ hideTitle = false, onEnabled }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { twoFactorEnabled, updateTwoFactorStatus } = useAuth();
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [secret, setSecret] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [authenticatorUri, setAuthenticatorUri] = useState('');
  const [code, setCode] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const handleSetup = async () => {
    try {
      setLoadingSetup(true);
      const setup = await authService.setupTwoFactor();
      setSecret(setup.secret ?? '');
      setQrCodeDataUrl(setup.qrCodeDataUrl?.startsWith('data:image/') ? setup.qrCodeDataUrl : '');
      setAuthenticatorUri(setup.authenticatorUri ?? '');
      if (!setup.secret && !setup.qrCodeDataUrl && !setup.authenticatorUri) {
        enqueueSnackbar('El servidor no devolvió los datos para configurar el autenticador', {
          variant: 'error',
        });
      }
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo iniciar la configuración del segundo factor'), {
        variant: 'error',
      });
    } finally {
      setLoadingSetup(false);
    }
  };

  const handleEnable = async () => {
    if (!/^\d{6}$/.test(code)) {
      enqueueSnackbar('Ingresa el código de 6 dígitos de tu autenticador', { variant: 'warning' });
      return;
    }
    try {
      setEnabling(true);
      await authService.enableTwoFactor(code);
      updateTwoFactorStatus(true);
      setCode('');
      enqueueSnackbar('Segundo factor activado correctamente', { variant: 'success' });
      onEnabled?.();
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo activar el segundo factor'), {
        variant: 'error',
      });
    } finally {
      setEnabling(false);
    }
  };

  const handleDisable = async () => {
    if (!currentPassword) {
      enqueueSnackbar('Ingresa tu contraseña actual', { variant: 'warning' });
      return;
    }
    if (!/^\d{6}$/.test(disableCode)) {
      enqueueSnackbar('Ingresa el código de 6 dígitos de tu autenticador', { variant: 'warning' });
      return;
    }
    try {
      setDisabling(true);
      await authService.disableTwoFactor(currentPassword, disableCode);
      updateTwoFactorStatus(false);
      setCurrentPassword('');
      setDisableCode('');
      enqueueSnackbar('Segundo factor desactivado correctamente', { variant: 'success' });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo desactivar el segundo factor'), {
        variant: 'error',
      });
    } finally {
      setDisabling(false);
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      enqueueSnackbar('Clave copiada', { variant: 'success' });
    } catch {
      enqueueSnackbar('No se pudo copiar la clave; puedes seleccionarla y copiarla manualmente', {
        variant: 'error',
      });
    }
  };

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 680 }}>
      {!hideTitle && <Typography variant="h6">Autenticación en dos pasos</Typography>}
      {twoFactorEnabled === true ? (
        <>
          <Alert severity="success">
            El segundo factor está activado. Para iniciar sesión, necesitarás el código de tu autenticador.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Para desactivarlo, confirma tu contraseña actual y el código de tu autenticador.
          </Typography>
          <TextField
            label="Contraseña actual"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoComplete="current-password"
            disabled={disabling}
            sx={{ maxWidth: 360 }}
          />
          <TextField
            label="Código del autenticador"
            value={disableCode}
            onChange={(event) => setDisableCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
            disabled={disabling}
            sx={{ maxWidth: 320 }}
          />
          <Button
            type="button"
            color="error"
            variant="outlined"
            onClick={handleDisable}
            disabled={disabling || !currentPassword || disableCode.length !== 6}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            {disabling ? <CircularProgress size={22} /> : 'Desactivar segundo factor'}
          </Button>
        </>
      ) : twoFactorEnabled === false ? (
        <>
          <Alert severity="info">
            Según tu último inicio de sesión, el backend no solicitó el segundo factor para esta cuenta.
          </Alert>
          {!secret && !qrCodeDataUrl && !authenticatorUri && (
            <Button
              type="button"
              variant="contained"
              onClick={handleSetup}
              disabled={loadingSetup}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              {loadingSetup ? <CircularProgress size={22} /> : 'Comenzar configuración'}
            </Button>
          )}

          {(secret || qrCodeDataUrl || authenticatorUri) && (
            <>
              <Typography variant="body2" color="text.secondary">
                Escanea el código con una aplicación autenticadora. Luego ingresa el código temporal
                para confirmar la activación.
              </Typography>

              {(qrCodeDataUrl || (!qrCodeDataUrl && authenticatorUri)) && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    width: 'fit-content',
                    bgcolor: 'common.white',
                  }}
                >
                  {qrCodeDataUrl ? (
                    <Box
                      component="img"
                      src={qrCodeDataUrl}
                      alt="Código QR para configurar el autenticador"
                      sx={{ display: 'block', width: 220, maxWidth: '100%' }}
                    />
                  ) : (
                    <QRCode value={authenticatorUri} size={220} />
                  )}
                </Paper>
              )}

              {secret && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <TextField
                    label="Clave para configuración manual"
                    value={secret}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopySecret}
                    sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
                  >
                    Copiar clave
                  </Button>
                </Stack>
              )}

              <TextField
                label="Código del autenticador"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
                disabled={enabling}
                sx={{ maxWidth: 320 }}
              />
              <Button
                type="button"
                variant="contained"
                onClick={handleEnable}
                disabled={enabling || code.length !== 6}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                {enabling ? <CircularProgress size={22} /> : 'Confirmar y activar'}
              </Button>
            </>
          )}
        </>
      ) : (
        <Alert severity="warning">
          No se conoce el estado de esta sesión porque no se inició sesión desde que se habilitó esta
          consulta. Cierra sesión y vuelve a ingresar para verificarlo; así se evitará reiniciar por error
          una configuración existente.
        </Alert>
      )}
    </Stack>
  );
};

export default SegundoFactorTab;
